import React, { useEffect, useState, useRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { isTokenExpiringSoon, getTokenExpirationTime } from '../utils/tokenUtils';

interface AuthGateProps {
  children: React.ReactNode;
  onUserInfo?: (userInfo: { name?: string; email?: string } | null) => void;
  onLogout?: () => void;
}

const AuthGate: React.FC<AuthGateProps> = ({ children, onUserInfo, onLogout }) => {
  const [idToken, setIdToken] = useState<string | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Prefer session token over Google ID token
    const sessionToken = localStorage.getItem('session_token');
    const idToken = localStorage.getItem('id_token');
    const token = sessionToken || idToken;
    setIdToken(token);
  }, []);

  // Auto-refresh token before expiration
  useEffect(() => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!idToken) {
      return;
    }

    const checkAndRefreshToken = () => {
      const token = localStorage.getItem('id_token');
      if (!token) {
        return;
      }

      // Check if token is expiring soon (within 10 minutes)
      if (isTokenExpiringSoon(token, 10)) {
        console.log('[AuthGate] Token expiring soon, attempting to refresh...');
        
        // Try to refresh session token
        fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          if (res.ok) {
            return res.json().then(data => {
              // Update session token
              localStorage.setItem('session_token', data.sessionToken);
              setIdToken(data.sessionToken);
              console.log('[AuthGate] Session token refreshed successfully');
            });
          } else {
            // If refresh fails, check if it's a Google ID token and try to create session
            const googleIdToken = localStorage.getItem('id_token');
            if (googleIdToken && googleIdToken !== token) {
              // Try to create new session from Google ID token
              return fetch('/api/auth/session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: googleIdToken })
              }).then(sessionRes => {
                if (sessionRes.ok) {
                  return sessionRes.json().then(data => {
                    localStorage.setItem('session_token', data.sessionToken);
                    setIdToken(data.sessionToken);
                    console.log('[AuthGate] New session created from Google ID token');
                  });
                } else {
                  // Both failed, need to re-login
                  console.log('[AuthGate] Token expired, clearing and showing login');
                  localStorage.removeItem('session_token');
                  localStorage.removeItem('id_token');
                  setIdToken(null);
                  onUserInfo?.(null);
                }
              });
            } else {
              // No Google ID token, need to re-login
              console.log('[AuthGate] Token expired, clearing and showing login');
              localStorage.removeItem('session_token');
              localStorage.removeItem('id_token');
              setIdToken(null);
              onUserInfo?.(null);
            }
          }
        }).catch(() => {
          // Network error, keep token for now
        });
        return;
      }

      // Schedule next check (check every 5 minutes)
      const expirationTime = getTokenExpirationTime(token);
      if (expirationTime) {
        const now = Date.now();
        const timeUntilExpiration = expirationTime - now;
        const refreshTime = Math.max(0, timeUntilExpiration - 10 * 60 * 1000); // Refresh 10 minutes before expiration
        const checkInterval = 5 * 60 * 1000; // Check every 5 minutes

        refreshTimerRef.current = setTimeout(() => {
          checkAndRefreshToken();
        }, Math.min(refreshTime, checkInterval));
      }
    };

    // Initial check
    checkAndRefreshToken();

    // Cleanup on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [idToken, onUserInfo]);

  // Проверка валидности токена при монтировании и при изменении idToken
  useEffect(() => {
    if (!idToken) {
      onUserInfo?.(null);
      return;
    }

    // Check token validity and fetch user info
    Promise.all([
      fetch('/api/connections/status', {
        headers: { Authorization: `Bearer ${idToken}` }
      }),
      fetch('/api/connections/user', {
        headers: { Authorization: `Bearer ${idToken}` }
      })
    ]).then(([statusRes, userRes]) => {
      if (statusRes.status === 401) {
        localStorage.removeItem('id_token');
        setIdToken(null);
        onUserInfo?.(null);
        return;
      }

      if (userRes.ok) {
        return userRes.json().then(userInfo => {
          onUserInfo?.(userInfo);
        });
      }
    }).catch(() => {
      // В случае сетевой ошибки тоже разлогиниваем
      localStorage.removeItem('id_token');
      setIdToken(null);
      onUserInfo?.(null);
    });
  }, [idToken, onUserInfo]);

  // Listen for global 401 events
  useEffect(() => {
    const handleUnauthorized = () => {
      localStorage.removeItem('session_token');
      localStorage.removeItem('id_token');
      setIdToken(null);
      onUserInfo?.(null);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [onUserInfo]);

  // logout function is now handled by parent component

  if (!idToken) {
    return (
      <div style={{ marginTop: 40, textAlign: 'center' }}>
        <h2>Please log in via Google for better user experience</h2>
        <GoogleLogin
          onSuccess={async (credentialResponse) => {
            if (credentialResponse.credential) {
              try {
                // Exchange Google ID token for session token
                const res = await fetch('/api/auth/session', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ idToken: credentialResponse.credential })
                });

                if (res.ok) {
                  const data = await res.json();
                  // Store session token instead of Google ID token
                  localStorage.setItem('session_token', data.sessionToken);
                  localStorage.setItem('id_token', credentialResponse.credential); // Keep for backward compatibility
                  setIdToken(data.sessionToken);

                  // Save user info in Firestore
                  fetch('/api/connections/save-user', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${data.sessionToken}` }
                  });
                } else {
                  console.error('Failed to create session');
                  alert('Failed to create session. Please try again.');
                }
              } catch (error) {
                console.error('Error creating session:', error);
                alert('Failed to create session. Please try again.');
              }
            }
          }}
          onError={() => alert('Google login failed')}
        />
      </div>
    );
  }

  return (
    <>
      {children}
    </>
  );
};

export default AuthGate;